import React, { useState, useRef, useCallback, useEffect } from 'react';
import './kehityslista.css';
import axios from 'axios';
import config from '../../devprodConfig';
import ConfirmModal from '../ConfirmModal';

const USERS = [
  { init: 'JR', bg: '#04aa00' },
  { init: 'HH', bg: '#1565c0' },
];

const COLS = [
  { id: 'tehtava',     label: 'Tehtävä'     },
  { id: 'kesken',      label: 'Kesken'       },
  { id: 'valmis',      label: 'Valmis'       },
  { id: 'palvelimella', label: 'Palvelimella' },
];

const TYPES = ['feature', 'fix', 'tuki'];
const TYPE_LABEL = { feature: 'Ominaisuus', fix: 'Korjaus', tuki: 'Tuki' };

function today() { return new Date().toISOString().slice(0, 10); }
function fmtD(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' });
}
function daysBetween(a, b, excludeWeekends = false) {
  if (!excludeWeekends) return Math.max(0, Math.round((new Date(b) - new Date(a)) / 86400000) + 1);
  let count = 0;
  const cur = new Date(a);
  const end = new Date(b);
  while (cur <= end) {
    const d = cur.getDay();
    if (d !== 0 && d !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return Math.max(0, count);
}
function numId(id) { return id.slice(1); } // 'p5' → '5', 't3' → '3'

const API_BASE = `${config.apiBaseUrl}/api/kanban`;
const axOpts = { withCredentials: true };
const apiGet    = path       => axios.get(`${API_BASE}${path}`, axOpts).then(r => r.data);
const apiPost   = (path, b)  => axios.post(`${API_BASE}${path}`, b, axOpts).then(r => r.data);
const apiPut    = (path, b)  => axios.put(`${API_BASE}${path}`, b, axOpts).then(r => r.data);
const apiDelete = path       => axios.delete(`${API_BASE}${path}`, axOpts).then(r => r.data);

const EMPTY_BOARD = {
  items: {},
  colOrder: { tehtava: [], kesken: [], valmis: [], palvelimella: [] },
  projTix: {},
};

export default function KehityslistaPage() {
  const S = useRef(JSON.parse(JSON.stringify(EMPTY_BOARD)));
  const [, setTick] = useState(0);
  const rerender = useCallback(() => setTick(t => t + 1), []);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  const [colForms, setColForms] = useState({});
  const [projForms, setProjForms] = useState({});
  const [colSort, setColSort] = useState({ tehtava: 'newest', kesken: 'newest', valmis: 'newest', palvelimella: 'newest' });
  const [renaming, setRenaming] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dropOverCol, setDropOverCol] = useState(null);
  const [dropOverProj, setDropOverProj] = useState(null);
  const [dropOverNested, setDropOverNested] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, label }
  const [mobileCol, setMobileCol] = useState('tehtava');
  const [moveMenu, setMoveMenu] = useState(null);
  const dragRef = useRef(null);

  const [excludeWeekends, setExcludeWeekendsState] = useState(() => {
    try { return localStorage.getItem('kl_noweekends') === '1'; } catch { return false; }
  });
  const toggleWeekends = () => setExcludeWeekendsState(v => {
    const next = !v;
    try { localStorage.setItem('kl_noweekends', next ? '1' : '0'); } catch {}
    return next;
  });

  const [filterUser, setFilterUserState] = useState(() => {
    try {
      const v = localStorage.getItem('kl_filter');
      const n = v !== null ? Number(v) : -1;
      return (n === -1 || (n >= 0 && n < USERS.length)) ? n : -1;
    } catch { return -1; }
  });
  const setFilterUser = (v) => {
    setFilterUserState(v);
    try { localStorage.setItem('kl_filter', String(v)); } catch {}
  };

  useEffect(() => {
    apiGet('/board')
      .then(data => { S.current = data; setLoading(false); rerender(); })
      .catch(err => {
        const msg = err?.response ? `${err.response.status} ${err.response.statusText}: ${JSON.stringify(err.response.data)}` : err.message;
        setApiError(`Taulukon lataus epäonnistui — ${msg}`);
        setLoading(false);
      });
  }, [rerender]);

  function MoveMenu({ id, status }) {
    if (moveMenu !== id) return null;
    return (
      <div className="kl-move-menu" onClick={e => e.stopPropagation()}>
        {COLS.filter(c => c.id !== status).map(c => (
          <button key={c.id} className="kl-move-opt" onClick={e => { e.stopPropagation(); dropOnCol(id, c.id); setMoveCol(c.id); setMoveMenu(null); }}>
            {c.label}
          </button>
        ))}
      </div>
    );
  }
  function setMoveCol(colId) { setMobileCol(colId); }

  function notifyError(action, err) {
    const msg = err?.response ? `${err.response.status} ${err.response.statusText}: ${JSON.stringify(err.response.data)}` : err.message;
    setApiError(`${action} epäonnistui — ${msg}`);
    console.error(action, err);
  }

  function isVisible(id) {
    if (filterUser === -1) return true;
    const it = S.current.items[id];
    if (!it) return false;
    if (it.assignee === null || it.assignee === 'all') return true;
    if (it.assignee === filterUser) return true;
    if (it.kind === 'project') return (S.current.projTix[id] || []).some(tid => {
      const t = S.current.items[tid];
      return t && (t.assignee === filterUser || t.assignee === 'all' || t.assignee === null);
    });
    return false;
  }

  // ── Mutations ─────────────────────────────────────────────
  function applyDates(item, toCol) {
    const now = today();
    if (toCol === 'kesken' || toCol === 'valmis' || toCol === 'palvelimella') {
      if (!item.startedAt) item.startedAt = now;
      if (item.assignee === null) item.assignee = 0;
    }
    if (toCol === 'valmis' || toCol === 'palvelimella') {
      if (!item.completedAt) item.completedAt = now;
    }
    item.status = toCol;
  }

  function dropOnCol(id, toCol) {
    const b = S.current;
    const it = b.items[id]; if (!it) return;
    if (it.kind === 'project') {
      b.colOrder[it.status] = b.colOrder[it.status].filter(x => x !== id);
      applyDates(it, toCol);
      if (!b.colOrder[toCol].includes(id)) b.colOrder[toCol].push(id);
      const tix = b.projTix[id] || [];
      tix.forEach(tid => { const t = b.items[tid]; if (t) applyDates(t, toCol); });
      apiPut(`/projects/${numId(id)}`, { status: toCol, startedAt: it.startedAt, completedAt: it.completedAt, assignee: it.assignee }).catch(e => notifyError('Projektin siirto', e));
      tix.forEach(tid => {
        const t = b.items[tid];
        if (t) apiPut(`/tickets/${numId(tid)}`, { status: toCol, startedAt: t.startedAt, completedAt: t.completedAt, assignee: t.assignee }).catch(e => notifyError('Osan siirto', e));
      });
    } else {
      if (it.projectId) b.projTix[it.projectId] = (b.projTix[it.projectId] || []).filter(x => x !== id);
      else b.colOrder[it.status] = b.colOrder[it.status].filter(x => x !== id);
      applyDates(it, toCol); it.projectId = null;
      if (!b.colOrder[toCol].includes(id)) b.colOrder[toCol].push(id);
      apiPut(`/tickets/${numId(id)}`, { status: toCol, startedAt: it.startedAt, completedAt: it.completedAt, assignee: it.assignee, projectId: null }).catch(e => notifyError('Osan siirto', e));
    }
    setDropOverCol(null); setDraggingId(null); rerender();
  }

  function nestTix(ticketId, projId) {
    const b = S.current;
    const t = b.items[ticketId], p = b.items[projId];
    if (!t || !p || t.kind !== 'ticket') return;
    if (t.projectId) b.projTix[t.projectId] = (b.projTix[t.projectId] || []).filter(x => x !== ticketId);
    else b.colOrder[t.status] = b.colOrder[t.status].filter(x => x !== ticketId);
    applyDates(t, p.status); t.projectId = projId;
    if (!b.projTix[projId]) b.projTix[projId] = [];
    if (!b.projTix[projId].includes(ticketId)) b.projTix[projId].push(ticketId);
    setDropOverProj(null); setDraggingId(null); rerender();
    apiPut(`/tickets/${numId(ticketId)}`, { status: t.status, projectId: projId, startedAt: t.startedAt, completedAt: t.completedAt, assignee: t.assignee }).catch(e => notifyError('Osan siirto projektiin', e));
  }

  function toggleExp(id) { const it = S.current.items[id]; if (it) { it.expanded = !it.expanded; rerender(); } }

  function cycleAva(id) {
    const it = S.current.items[id]; if (!it) return;
    if (it.assignee === null) it.assignee = 0;
    else if (it.assignee === 'all') it.assignee = null;
    else if (it.assignee === USERS.length - 1) it.assignee = 'all';
    else it.assignee = it.assignee + 1;
    rerender();
    const path = it.kind === 'project' ? `/projects/${numId(id)}` : `/tickets/${numId(id)}`;
    apiPut(path, { assignee: it.assignee }).catch(e => notifyError('Vastuuhenkilön vaihto', e));
  }

  function cycleType(id) {
    const it = S.current.items[id]; if (!it) return;
    it.type = TYPES[(TYPES.indexOf(it.type) + 1) % TYPES.length]; rerender();
    apiPut(`/tickets/${numId(id)}`, { type: it.type }).catch(e => notifyError('Tyypin vaihto', e));
  }

  function deleteItem(id) {
    const b = S.current; const it = b.items[id]; if (!it) return;
    const label = it.kind === 'project' ? `projekti "${it.name}"` : `osa "${it.name}"`;
    setConfirmDelete({ id, label });
  }

  function doDeleteItem(id) {
    const b = S.current; const it = b.items[id]; if (!it) return;
    if (it.kind === 'project') {
      (b.projTix[id] || []).forEach(tid => delete b.items[tid]);
      delete b.projTix[id];
      b.colOrder[it.status] = b.colOrder[it.status].filter(x => x !== id);
      apiDelete(`/projects/${numId(id)}`).catch(e => notifyError('Projektin poisto', e));
    } else {
      if (it.projectId) b.projTix[it.projectId] = (b.projTix[it.projectId] || []).filter(x => x !== id);
      else b.colOrder[it.status] = b.colOrder[it.status].filter(x => x !== id);
      apiDelete(`/tickets/${numId(id)}`).catch(e => notifyError('Osan poisto', e));
    }
    delete b.items[id]; rerender();
  }

  function commitRename(id, value) {
    const it = S.current.items[id];
    if (it && value.trim()) {
      it.name = value.trim();
      const path = it.kind === 'project' ? `/projects/${numId(id)}` : `/tickets/${numId(id)}`;
      apiPut(path, { name: it.name }).catch(e => notifyError('Nimen muutos', e));
    }
    setRenaming(null); rerender();
  }

  async function commitColAdd(colId) {
    const f = colForms[colId]; if (!f || !f.name.trim()) return;
    const b = S.current; const now = today();
    const assignee = colId === 'tehtava' ? null : 0;
    const startedAt = colId !== 'tehtava' ? now : null;
    const completedAt = (colId === 'valmis' || colId === 'palvelimella') ? now : null;
    setColForms(prev => ({ ...prev, [colId]: null }));
    try {
      if (f.kind === 'project') {
        const res = await apiPost('/projects', { name: f.name.trim(), status: colId, assignee, createdAt: now, startedAt, completedAt });
        const rid = res.id;
        b.items[rid] = { id: rid, kind: 'project', name: f.name.trim(), desc: '', status: colId, expanded: true, assignee, createdAt: now, startedAt, completedAt };
        b.projTix[rid] = [];
        if (!b.colOrder[colId].includes(rid)) b.colOrder[colId].push(rid);
      } else {
        const res = await apiPost('/tickets', { name: f.name.trim(), type: f.type, status: colId, projectId: null, assignee, createdAt: now, startedAt, completedAt });
        const rid = res.id;
        b.items[rid] = { id: rid, kind: 'ticket', type: f.type, name: f.name.trim(), status: colId, projectId: null, assignee, createdAt: now, startedAt, completedAt };
        if (!b.colOrder[colId].includes(rid)) b.colOrder[colId].push(rid);
      }
      rerender();
    } catch (err) { notifyError('Luonti', err); }
  }

  async function commitProjAdd(projId) {
    const f = projForms[projId]; if (!f || !f.name.trim()) return;
    const b = S.current; const p = b.items[projId]; if (!p) return;
    const now = today(); const colId = p.status;
    const assignee = colId === 'tehtava' ? null : 0;
    const startedAt = colId !== 'tehtava' ? now : null;
    const completedAt = (colId === 'valmis' || colId === 'palvelimella') ? now : null;
    setProjForms(prev => ({ ...prev, [projId]: null }));
    try {
      const res = await apiPost('/tickets', { name: f.name.trim(), type: f.type, status: colId, projectId: projId, assignee, createdAt: now, startedAt, completedAt });
      const rid = res.id;
      b.items[rid] = { id: rid, kind: 'ticket', type: f.type, name: f.name.trim(), status: colId, projectId: projId, assignee, createdAt: now, startedAt, completedAt };
      if (!b.projTix[projId]) b.projTix[projId] = [];
      b.projTix[projId].push(rid);
      rerender();
    } catch (err) { notifyError('Osan luonti', err); }
  }

  // ── Drag handlers (col-level) ─────────────────────────────
  function onColDragOver(e, colId) {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
    if (dropOverCol !== colId) setDropOverCol(colId);
  }
  function onColDragLeave(e) {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropOverCol(null);
  }
  function onColDrop(e, colId) {
    e.preventDefault(); if (dragRef.current) dropOnCol(dragRef.current.id, colId);
  }

  function reorderNested(projId, draggedId, insertBefore) {
    const b = S.current;
    const arr = b.projTix[projId] || [];
    const filtered = arr.filter(x => x !== draggedId);
    const to = insertBefore ? filtered.indexOf(insertBefore) : filtered.length;
    filtered.splice(to < 0 ? filtered.length : to, 0, draggedId);
    b.projTix[projId] = filtered;
    setDropOverNested(null); setDraggingId(null); rerender();
    const posItems = filtered.map((id, i) => ({ id, position: i }));
    apiPost('/positions', { items: posItems }).catch(e => notifyError('Järjestyksen tallennus', e));
  }

  function onNestedDragOver(e, projId, targetId) {
    if (dragRef.current?.kind !== 'ticket') return;
    e.preventDefault(); e.stopPropagation();
    const dragged = S.current.items[dragRef.current.id];
    if (dragged?.projectId === projId && dragRef.current.id !== targetId) {
      const rect = e.currentTarget.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      const arr = S.current.projTix[projId] || [];
      const idx = arr.indexOf(targetId);
      const insertBefore = e.clientY < mid ? targetId : (arr[idx + 1] || null);
      setDropOverNested({ projId, insertBefore });
    } else {
      setDropOverProj(projId);
    }
  }
  function onNestedDrop(e, projId, targetId) {
    e.preventDefault(); e.stopPropagation();
    if (dragRef.current?.kind !== 'ticket') return;
    const dragged = S.current.items[dragRef.current.id];
    if (dragged?.projectId === projId) {
      const arr = S.current.projTix[projId] || [];
      const idx = arr.indexOf(targetId);
      const rect = e.currentTarget.getBoundingClientRect();
      const insertBefore = e.clientY < rect.top + rect.height / 2 ? targetId : (arr[idx + 1] || null);
      reorderNested(projId, dragRef.current.id, insertBefore);
    } else {
      nestTix(dragRef.current.id, projId);
    }
  }

  function onProjDropZoneOver(e, projId) {
    if (dragRef.current?.kind !== 'ticket') return;
    e.preventDefault(); e.stopPropagation();
    const dragged = S.current.items[dragRef.current.id];
    if (dragged?.projectId === projId) {
      setDropOverNested({ projId, insertBefore: null });
    } else {
      if (dropOverProj !== projId) setDropOverProj(projId);
    }
  }
  function onProjDropZoneDrop(e, projId) {
    e.preventDefault(); e.stopPropagation();
    if (dragRef.current?.kind !== 'ticket') return;
    const dragged = S.current.items[dragRef.current.id];
    if (dragged?.projectId === projId) {
      reorderNested(projId, dragRef.current.id, null);
    } else {
      nestTix(dragRef.current.id, projId);
    }
  }

  // ── Chips helper ─────────────────────────────────────────
  function avaLabel(assignee) {
    if (assignee === null) return { init: '—', bg: '#bbb' };
    if (assignee === 'all') return { init: '—', bg: '#777' };
    return USERS[assignee];
  }

  function chips(item) {
    const u = avaLabel(item.assignee);
    const dur = item.completedAt && item.startedAt ? daysBetween(item.startedAt, item.completedAt, excludeWeekends) : null;

    function dateChip(field, icon, cls, tooltip) {
      const val = item[field];
      if (!val) return null;
      if (editingDate?.id === item.id && editingDate?.field === field) {
        return (
          <input key={field + '-inp'} type="date" className="kl-chip-date-inp" autoFocus
            defaultValue={val}
            onBlur={e => {
              const it = S.current.items[item.id];
              if (it) {
                const val = e.target.value || null;
                it[field] = val;
                const path = it.kind === 'project' ? `/projects/${numId(item.id)}` : `/tickets/${numId(item.id)}`;
                apiPut(path, { [field]: val }).catch(er => notifyError('Päivämäärän tallennus', er));
              }
              setEditingDate(null); rerender();
            }}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditingDate(null); }}
            onClick={e => e.stopPropagation()} />
        );
      }
      return (
        <span key={field} className={`kl-chip ${cls} kl-chip-editable`}
          title={tooltip || 'Klikkaa muokataksesi'}
          onClick={e => { e.stopPropagation(); setEditingDate({ id: item.id, field }); }}>
          {icon} {fmtD(val)}
        </span>
      );
    }

    return (
      <>
        <button className="kl-ava" style={{ background: u.bg }} onClick={e => { e.stopPropagation(); cycleAva(item.id); }} title="Vaihda vastuuhenkilö">{u.init}</button>
        {dateChip('createdAt',   '🗓', 'kl-chip-c', 'Luontipäivä — klikkaa muokataksesi')}
        {dateChip('startedAt',   '▶',  'kl-chip-s', 'Aloituspäivä — klikkaa muokataksesi')}
        {dateChip('completedAt', '✓',  'kl-chip-d', 'Valmistumispäivä — klikkaa muokataksesi')}
        {dur !== null && <span className="kl-chip kl-chip-r" title="Kesto aloituksesta valmistumiseen">{dur}pv</span>}
      </>
    );
  }

  // ── Render ───────────────────────────────────────────────
  const b = S.current;

  if (loading) {
    return <div className="kl-page"><div style={{ padding: '2rem', color: 'var(--tx)' }}>Ladataan...</div></div>;
  }

  return (
    <div className="kl-page">

      {apiError && (
        <div className="kl-error-banner">
          <span>{apiError}</span>
          <button onClick={() => setApiError(null)}>✕</button>
        </div>
      )}

      {/* Filter bar */}
      <div className="kl-filterbar">
        <span className="kl-filter-label">Näytä:</span>
        <button className={`kl-filter-btn${filterUser === -1 ? ' active' : ''}`} onClick={() => setFilterUser(-1)}>
          {filterUser === -1 && <span className="kl-filter-check">✓</span>}Kaikki
        </button>
        {USERS.map((u, i) => (
          <button key={i} className={`kl-filter-ava${filterUser === i ? ' active' : ''}`}
            style={{ background: u.bg }} onClick={() => setFilterUser(i)} title={u.init}>{u.init}</button>
        ))}
        <button className={`kl-filter-btn kl-weekends-btn${excludeWeekends ? ' active' : ''}`}
          onClick={toggleWeekends} title="Poista viikonloput päivälaskennasta">
          {excludeWeekends && <span className="kl-filter-check">✓</span>}Vain arkipäivät
        </button>
      </div>

      {/* Mobile column tabs */}
      <div className="kl-mobile-tabs">
        {COLS.map(col => {
          const count = (b.colOrder[col.id] || []).filter(id => isVisible(id)).length;
          return (
            <button key={col.id}
              className={`kl-mobile-tab${mobileCol === col.id ? ' active' : ''}`}
              onClick={() => setMobileCol(col.id)}>
              {col.label}
              {count > 0 && <span className="kl-mobile-tab-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Board */}
      <div className="kl-board">
        {COLS.map(col => {
          const ids = [...(b.colOrder[col.id] || []).filter(id => isVisible(id))].sort((x, y) => {
            const dx = b.items[x]?.createdAt || '';
            const dy = b.items[y]?.createdAt || '';
            return colSort[col.id] === 'newest' ? dy.localeCompare(dx) : dx.localeCompare(dy);
          });
          let count = 0;
          ids.forEach(id => { count++; if (b.items[id]?.kind === 'project') count += (b.projTix[id] || []).length; });
          const cf = colForms[col.id];

          return (
            <div key={col.id} className={`kl-col${dropOverCol === col.id ? ' drop-over' : ''}${mobileCol !== col.id ? ' kl-col-mobile-hidden' : ''}`}
              onDragOver={e => onColDragOver(e, col.id)}
              onDragLeave={onColDragLeave}
              onDrop={e => onColDrop(e, col.id)}>

              {/* Column header */}
              <div className="kl-col-hd">
                <span className="kl-col-title">{col.label}</span>
                <span className="kl-col-count">{count}</span>
                <button className="kl-col-sortbtn"
                  title={colSort[col.id] === 'newest' ? 'Uusin ensin — vaihda vanhimpaan' : 'Vanhin ensin — vaihda uusimpaan'}
                  onClick={() => setColSort(prev => ({ ...prev, [col.id]: prev[col.id] === 'newest' ? 'oldest' : 'newest' }))}>
                  {colSort[col.id] === 'newest' ? '↓' : '↑'}
                </button>
                <button className="kl-col-addbtn" title="Lisää"
                  onClick={() => setColForms(prev => ({ ...prev, [col.id]: prev[col.id] ? null : { kind: 'project', type: 'feature', name: '' } }))}>+</button>
              </div>

              {/* Column body */}
              <div className="kl-col-body">

                {/* Add form */}
                {cf && (
                  <div className="kl-col-add-form">
                    <input autoFocus className="kl-col-add-inp"
                      placeholder={cf.kind === 'project' ? 'Projektin nimi...' : 'Osan nimi...'}
                      value={cf.name}
                      onChange={e => setColForms(prev => ({ ...prev, [col.id]: { ...prev[col.id], name: e.target.value } }))}
                      onKeyDown={e => { if (e.key === 'Enter') commitColAdd(col.id); if (e.key === 'Escape') setColForms(prev => ({ ...prev, [col.id]: null })); }} />
                    <div className="kl-col-add-row">
                      <button className={`kl-kind-btn kp${cf.kind === 'project' ? ' on' : ''}`}
                        onClick={() => setColForms(prev => ({ ...prev, [col.id]: { ...prev[col.id], kind: 'project' } }))}>Projekti</button>
                      <button className={`kl-kind-btn kt${cf.kind === 'ticket' ? ' on' : ''}`}
                        onClick={() => setColForms(prev => ({ ...prev, [col.id]: { ...prev[col.id], kind: 'ticket' } }))}>Osa</button>
                      {cf.kind === 'ticket' && (
                        <select className="kl-col-add-sel" value={cf.type}
                          onChange={e => setColForms(prev => ({ ...prev, [col.id]: { ...prev[col.id], type: e.target.value } }))}>
                          {TYPES.map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                        </select>
                      )}
                      <button className="kl-col-add-x" onClick={() => setColForms(prev => ({ ...prev, [col.id]: null }))}>✕</button>
                      <button className="kl-col-add-ok" onClick={() => commitColAdd(col.id)}>Lisää</button>
                    </div>
                  </div>
                )}

                {/* Items */}
                {ids.map(id => {
                  const it = b.items[id]; if (!it) return null;

                  if (it.kind === 'project') {
                    const tickets = (b.projTix[it.id] || []).map(tid => b.items[tid]).filter(Boolean);
                    const pf = projForms[it.id];
                    return (
                      <div key={it.id}
                        className={`kl-card kl-project${draggingId === it.id ? ' dragging' : ''}`}
                        draggable={renaming === null || (renaming.id !== it.id && !(b.projTix[it.id] || []).includes(renaming.id))}
                        onDragStart={e => { dragRef.current = { id: it.id, kind: 'project' }; setDraggingId(it.id); e.dataTransfer.effectAllowed = 'move'; }}
                        onDragEnd={() => { dragRef.current = null; setDraggingId(null); setDropOverCol(null); setDropOverNested(null); }}
                        onDragOver={e => { if (dragRef.current?.kind === 'ticket') { e.preventDefault(); e.stopPropagation(); } }}
                        onDrop={e => {
                          if (dragRef.current?.kind !== 'ticket') return;
                          e.preventDefault(); e.stopPropagation();
                          const dragged = S.current.items[dragRef.current.id];
                          if (dragged?.projectId === it.id) reorderNested(it.id, dragRef.current.id, null);
                          else nestTix(dragRef.current.id, it.id);
                        }}>

                        <div className="kl-card-hd">
                          <button className={`kl-expand${it.expanded ? '' : ' closed'}`} onClick={e => { e.stopPropagation(); toggleExp(it.id); }}>▾</button>
                          {renaming?.id === it.id
                            ? <input autoFocus className="kl-rename-inp" value={renaming.value}
                                onChange={e => setRenaming(r => ({ ...r, value: e.target.value }))}
                                onBlur={() => commitRename(it.id, renaming.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRename(it.id, renaming.value); } if (e.key === 'Escape') setRenaming(null); }} />
                            : <span className="kl-title" onDoubleClick={e => { e.stopPropagation(); setRenaming({ id: it.id, value: it.name }); }}>{it.name}</span>
                          }
                          <button className="kl-move-btn" title="Siirrä" onClick={e => { e.stopPropagation(); setMoveMenu(moveMenu === it.id ? null : it.id); }}>⇄</button>
                          <button className="kl-del" onClick={e => { e.stopPropagation(); deleteItem(it.id); }} title="Poista">✕</button>
                        </div>
                        <MoveMenu id={it.id} status={it.status} />

                        {it.desc && <div className="kl-desc">{it.desc}</div>}
                        <div className="kl-meta">{chips(it)}</div>

                        {it.expanded && (
                          <div className="kl-proj-body">
                            {tickets.map(t => (
                              <React.Fragment key={t.id}>
                              {dropOverNested?.projId === it.id && dropOverNested?.insertBefore === t.id && (
                                <div className="kl-nest-insert-line" />
                              )}
                              <div
                                className={`kl-nested ${t.type}${draggingId === t.id ? ' dragging' : ''}`}
                                draggable={renaming?.id !== t.id}
                                onDragStart={e => { e.stopPropagation(); dragRef.current = { id: t.id, kind: 'ticket' }; setDraggingId(t.id); e.dataTransfer.effectAllowed = 'move'; }}
                                onDragEnd={() => { dragRef.current = null; setDraggingId(null); setDropOverProj(null); setDropOverNested(null); }}
                                onDragOver={e => onNestedDragOver(e, it.id, t.id)}
                                onDrop={e => onNestedDrop(e, it.id, t.id)}>
                                <button className={`kl-nt-dot ${t.type}`} onClick={e => { e.stopPropagation(); cycleType(t.id); }} title="Vaihda tyyppi" />
                                {renaming?.id === t.id
                                  ? <input autoFocus className="kl-rename-inp kl-nt-rename" value={renaming.value}
                                      onChange={e => setRenaming(r => ({ ...r, value: e.target.value }))}
                                      onBlur={() => commitRename(t.id, renaming.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRename(t.id, renaming.value); } if (e.key === 'Escape') setRenaming(null); }}
                                      onClick={e => e.stopPropagation()} />
                                  : <span className="kl-nt-name" onDoubleClick={e => { e.stopPropagation(); setRenaming({ id: t.id, value: t.name }); }}>{t.name}</span>
                                }
                                <button className="kl-ava kl-nt-ava" style={{ background: avaLabel(t.assignee).bg }} onClick={e => { e.stopPropagation(); cycleAva(t.id); }}>{avaLabel(t.assignee).init}</button>
                                <button className="kl-move-btn" title="Siirrä" onClick={e => { e.stopPropagation(); setMoveMenu(moveMenu === t.id ? null : t.id); }}>⇄</button>
                                <button className="kl-del" onClick={e => { e.stopPropagation(); deleteItem(t.id); }} title="Poista">✕</button>
                              </div>
                              {moveMenu === t.id && <MoveMenu id={t.id} status={t.status} />}
                              </React.Fragment>
                            ))}

                            {dropOverNested?.projId === it.id && dropOverNested?.insertBefore === null && (
                              <div className="kl-nest-insert-line" />
                            )}
                            <div className={`kl-proj-drop${dropOverProj === it.id ? ' active' : ''}`}
                              onDragOver={e => onProjDropZoneOver(e, it.id)}
                              onDragLeave={() => setDropOverProj(null)}
                              onDrop={e => onProjDropZoneDrop(e, it.id)}>
                              Pudota osa tähän
                            </div>

                            {pf ? (
                              <div className="kl-inline-row">
                                <input autoFocus className="kl-inline-inp" placeholder="Osan nimi..." value={pf.name}
                                  onChange={e => setProjForms(prev => ({ ...prev, [it.id]: { ...prev[it.id], name: e.target.value } }))}
                                  onKeyDown={e => { if (e.key === 'Enter') commitProjAdd(it.id); if (e.key === 'Escape') setProjForms(prev => ({ ...prev, [it.id]: null })); }} />
                                <select className="kl-inline-sel" value={pf.type}
                                  onChange={e => setProjForms(prev => ({ ...prev, [it.id]: { ...prev[it.id], type: e.target.value } }))}>
                                  {TYPES.map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                                </select>
                                <button className="kl-inline-ok" onClick={() => commitProjAdd(it.id)}>+</button>
                                <button className="kl-inline-x" onClick={() => setProjForms(prev => ({ ...prev, [it.id]: null }))}>✕</button>
                              </div>
                            ) : (
                              <button className="kl-add-nested-btn" onClick={e => { e.stopPropagation(); setProjForms(prev => ({ ...prev, [it.id]: { type: 'feature', name: '' } })); }}>+ Lisää osa</button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // standalone ticket
                  return (
                    <div key={it.id}
                      className={`kl-card kl-ticket ${it.type}${draggingId === it.id ? ' dragging' : ''}`}
                      draggable={renaming?.id !== it.id}
                      onDragStart={e => { dragRef.current = { id: it.id, kind: 'ticket' }; setDraggingId(it.id); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragEnd={() => { dragRef.current = null; setDraggingId(null); setDropOverCol(null); setDropOverNested(null); }}>

                      <div className="kl-card-hd">
                        {renaming?.id === it.id
                          ? <input autoFocus className="kl-rename-inp" value={renaming.value}
                              onChange={e => setRenaming(r => ({ ...r, value: e.target.value }))}
                              onBlur={() => commitRename(it.id, renaming.value)}
                              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitRename(it.id, renaming.value); } if (e.key === 'Escape') setRenaming(null); }} />
                          : <span className="kl-title" onDoubleClick={e => { e.stopPropagation(); setRenaming({ id: it.id, value: it.name }); }}>{it.name}</span>
                        }
                        <button className="kl-move-btn" title="Siirrä" onClick={e => { e.stopPropagation(); setMoveMenu(moveMenu === it.id ? null : it.id); }}>⇄</button>
                        <button className="kl-del" onClick={e => { e.stopPropagation(); deleteItem(it.id); }} title="Poista">✕</button>
                      </div>
                      <MoveMenu id={it.id} status={it.status} />

                      <div className="kl-meta">
                        <button className={`kl-tag ${it.type}`} onClick={e => { e.stopPropagation(); cycleType(it.id); }}>{TYPE_LABEL[it.type]}</button>
                        {chips(it)}
                      </div>
                    </div>
                  );
                })}

                <div className="kl-spacer" />
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmModal
        show={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => { doDeleteItem(confirmDelete.id); setConfirmDelete(null); }}
        title="Poista"
        message={`Poistetaanko ${confirmDelete?.label}?`}
        confirmText="Poista"
        cancelText="Peruuta"
        confirmVariant="danger"
      />
    </div>
  );
}
